/**
 * BulkImportEmployeesDialog — admin bulk-onboards employees from a CSV.
 *
 * Expected CSV columns (case-insensitive headers):
 *   • Common:      name, email, phone, kind (admin|staff), state
 *   • Admin only:  department, position
 *   • Staff only:  role (Valet|Cleaner), position, region
 *
 * Each row is sent to POST /api/company/employees with `employee_kind`,
 * which auto-assigns a structured badge AND routes the user to the right
 * destination on login (admin → NECS Admin, supervisor/crew_leader → Driver app,
 * agent → Site Agent app).
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Upload, FileText, CheckCircle2, XCircle, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map staff position label → internal user role so login redirects correctly
const STAFF_ROLE_TO_USER_ROLE = {
  'Regional Manager': 'supervisor',
  'Regional Supervisor': 'supervisor',
  'Crew Leader': 'crew_leader',
  'Site Agent': 'agent',
};

const SAMPLE_CSV = [
  'name,email,phone,kind,department,position,role,region,state',
  'John Smith,john.smith@necs.com,+12145550001,admin,Operations,Director,,,TX',
  'Maria Lopez,maria.l@necs.com,+12145550002,admin,Finance & Accounting,Assistant Director,,,TX',
  'Carlos Diaz,carlos.d@necs.com,+12145550010,staff,,Site Agent,Valet,NTX,TX',
  'Aisha Khan,aisha.k@necs.com,+12145550011,staff,,Crew Leader,Cleaner,ETX,TX',
].join('\n');

// ───────── tiny CSV parser (handles quoted fields, commas, escaped quotes) ─────────
function parseCSV(text) {
  const rows = [];
  let cur = [''], inQ = false, i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i += 2; continue; }
      if (ch === '"') { inQ = false; i++; continue; }
      cur[cur.length - 1] += ch; i++; continue;
    }
    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ',') { cur.push(''); i++; continue; }
    if (ch === '\n' || ch === '\r') {
      if (cur.length > 1 || cur[0] !== '') rows.push(cur);
      cur = [''];
      if (ch === '\r' && text[i + 1] === '\n') i += 2; else i++;
      continue;
    }
    cur[cur.length - 1] += ch; i++;
  }
  if (cur.length > 1 || cur[0] !== '') rows.push(cur);
  if (rows.length === 0) return { headers: [], records: [] };
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const records = rows.slice(1).map((row) => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (row[idx] || '').trim(); });
    return obj;
  });
  return { headers, records };
}

export default function BulkImportEmployeesDialog({ onCompleted }) {
  const [open, setOpen] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [records, setRecords] = useState([]);
  const [filename, setFilename] = useState('');
  const [results, setResults] = useState([]); // [{ email, ok, badge, error }]
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setRecords([]); setFilename(''); setResults([]); setImporting(false); setParsing(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setFilename(file.name);
    setResults([]);
    try {
      const text = await file.text();
      const { records: rows } = parseCSV(text);
      if (rows.length === 0) {
        toast.error('No data rows found in the CSV.');
        setRecords([]);
      } else {
        setRecords(rows);
        toast.success(`Loaded ${rows.length} rows from ${file.name}`);
      }
    } catch (err) {
      toast.error('Failed to parse CSV: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'necs_employees_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build the payload for one row depending on kind=admin|staff
  const buildPayload = (row) => {
    const kind = (row.kind || '').toLowerCase() === 'admin' ? 'admin' : 'staff';
    const common = {
      employee_kind: kind,
      name: row.name,
      email: row.email,
      phone: row.phone || '',
      state: row.state || '',
      position: row.position || '',
    };
    if (kind === 'admin') {
      return { ...common, role: 'admin', department: row.department || '', assigned_shift: 'admin_office' };
    }
    const userRole = STAFF_ROLE_TO_USER_ROLE[row.position] || 'agent';
    return {
      ...common,
      role: userRole,
      role_type: row.role || '',
      region: row.region || '',
      assigned_shift: 'shift_1',
    };
  };

  const runImport = async () => {
    if (records.length === 0) return;
    setImporting(true);
    setResults([]);
    const out = [];
    for (const row of records) {
      // Basic guardrails
      if (!row.name || !row.email) {
        out.push({ email: row.email || '(missing email)', ok: false, error: 'Missing name or email' });
        setResults([...out]);
        continue;
      }
      try {
        const res = await fetch(`${BACKEND_URL}/api/company/employees`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload(row)),
        });
        const data = await res.json();
        if (!res.ok) {
          out.push({ email: row.email, ok: false, error: data?.detail || 'Failed' });
        } else {
          out.push({
            email: row.email,
            ok: true,
            badge: data?.employee?.badge_number,
            temp_password: data?.employee?.temp_password,
          });
        }
      } catch (err) {
        out.push({ email: row.email, ok: false, error: err.message });
      }
      setResults([...out]);
    }
    setImporting(false);
    const success = out.filter((r) => r.ok).length;
    const failed = out.length - success;
    toast.success(`${success} imported · ${failed} failed`);
    onCompleted && onCompleted({ success, failed, results: out });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="font-bold border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962] hover:text-black"
          data-testid="bulk-import-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-3xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import Employees from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="p-4 bg-zinc-800 border-zinc-700">
            <p className="text-sm text-zinc-300 mb-2">
              CSV columns (case-insensitive headers):{' '}
              <span className="font-mono text-[#C9A962]">name, email, phone, kind, department, position, role, region, state</span>
            </p>
            <ul className="text-xs text-zinc-400 list-disc list-inside space-y-1">
              <li><strong>kind</strong>: <code>admin</code> or <code>staff</code> (drives badge format and login destination).</li>
              <li><strong>Admin rows</strong>: fill <code>department</code> + <code>position</code> (CEO/Director/Manager/Asst Director/Admin Agent) + <code>state</code>. Shift auto = Mon–Fri 8:30 AM–4:30 PM.</li>
              <li><strong>Staff rows</strong>: fill <code>role</code> (Valet/Cleaner) + <code>position</code> (Regional Manager/Supervisor/Crew Leader/Site Agent) + <code>region</code> + <code>state</code>.</li>
            </ul>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadSample}
              className="mt-3 border-zinc-600"
              data-testid="bulk-import-download-sample"
            >
              <Download className="h-3 w-3 mr-1" /> Download sample template
            </Button>
          </Card>

          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full text-sm text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-bold file:bg-[#C9A962] file:text-black hover:file:opacity-90"
              data-testid="bulk-import-file-input"
            />
            {filename && (
              <p className="text-xs text-zinc-400 mt-2 flex items-center gap-1">
                <FileText className="h-3 w-3" /> {filename} — {records.length} rows ready
              </p>
            )}
          </div>

          {records.length > 0 && results.length === 0 && (
            <div className="max-h-60 overflow-y-auto border border-zinc-700 rounded">
              <table className="w-full text-xs">
                <thead className="bg-zinc-800 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">Name</th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">Kind</th>
                    <th className="px-2 py-1 text-left">Position</th>
                    <th className="px-2 py-1 text-left">Dept / Role</th>
                    <th className="px-2 py-1 text-left">State / Region</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, idx) => (
                    <tr key={idx} className="border-t border-zinc-800" data-testid={`bulk-import-row-${idx}`}>
                      <td className="px-2 py-1">{r.name}</td>
                      <td className="px-2 py-1">{r.email}</td>
                      <td className="px-2 py-1 uppercase text-[#C9A962]">{r.kind || 'staff'}</td>
                      <td className="px-2 py-1">{r.position}</td>
                      <td className="px-2 py-1">{r.department || r.role}</td>
                      <td className="px-2 py-1">{[r.region, r.state].filter(Boolean).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {results.length > 0 && (
            <div className="max-h-60 overflow-y-auto border border-zinc-700 rounded">
              <table className="w-full text-xs" data-testid="bulk-import-results-table">
                <thead className="bg-zinc-800 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left w-8"></th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">Badge</th>
                    <th className="px-2 py-1 text-left">Temp password / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, idx) => (
                    <tr key={idx} className="border-t border-zinc-800">
                      <td className="px-2 py-1">
                        {r.ok
                          ? <CheckCircle2 className="h-4 w-4 text-green-500" data-testid={`result-ok-${idx}`} />
                          : <XCircle className="h-4 w-4 text-red-500" data-testid={`result-fail-${idx}`} />}
                      </td>
                      <td className="px-2 py-1">{r.email}</td>
                      <td className="px-2 py-1 font-mono text-[#C9A962]">{r.badge || '—'}</td>
                      <td className="px-2 py-1 text-zinc-300">{r.ok ? r.temp_password : r.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" className="text-zinc-300" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              type="button"
              onClick={runImport}
              disabled={records.length === 0 || importing || parsing}
              className="bg-[#C9A962] text-black hover:opacity-90 font-bold"
              data-testid="bulk-import-run"
            >
              {importing ? `Importing… (${results.length}/${records.length})` : `Import ${records.length || ''} rows`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
